import { TestOrder, TestOrderStock, PurchaseInvoice, InvoiceStockItem } from "../types";

const API_BASE = "/1c-api/odata/standard.odata/";

import { getFixedAuthToken, getAuthorizationHeader } from "./authConfig";

const get1CAuthHeader = (): string => getAuthorizationHeader(getFixedAuthToken());

// --- Helpers ---

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const fetchWithRetry = async (url: string, options: RequestInit, retries = 3): Promise<Response> => {
  try {
    const response = await fetch(url, options);
    if (response.status >= 500) throw new Error(`Server Error: ${response.status}`);
    return response;
  } catch (err: any) {
    if (err.name === 'AbortError') throw err;
    if (retries > 0) {
      await delay(1000);
      return fetchWithRetry(url, options, retries - 1);
    }
    throw err;
  }
};

const BATCH_SIZE = 15;

const fetchEntityMap = async (
  resource: string,
  keys: string[],
  selectFields: string,
  signal?: AbortSignal
): Promise<Record<string, any>> => {
  if (keys.length === 0) return {};
  const uniqueKeys = [...new Set(keys)];
  const result: Record<string, any> = {};

  for (let i = 0; i < uniqueKeys.length; i += BATCH_SIZE) {
    const batch = uniqueKeys.slice(i, i + BATCH_SIZE);
    const baseFilter = batch.map(k => `Ref_Key eq guid'${k}'`).join(' or ');
    const filter = resource === "Catalog_Номенклатура"
      ? baseFilter
      : `(${baseFilter}) and DeletionMark eq false`;
    const url = `${API_BASE}${resource}?$select=Ref_Key,${selectFields}&$filter=${encodeURIComponent(filter)}&$format=json`;

    try {
      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: { 'Authorization': get1CAuthHeader(), 'Accept': 'application/json' },
        signal
      });
      if (response.ok) {
        const data = await response.json();
        (data.value || []).forEach((item: any) => { result[item.Ref_Key] = item; });
      }
    } catch (e: any) {
      if (e.name === 'AbortError') throw e;
      console.warn(`Failed to fetch batch from ${resource}`, e);
    }
  }
  return result;
};

// --- Nomenclature (Description + Eng parallel) ---

const fetchNomenclatureDetails = async (
  keys: string[],
  signal?: AbortSignal
): Promise<Record<string, { name: string; eng: string }>> => {
  const result: Record<string, { name: string; eng: string }> = {};

  for (let i = 0; i < keys.length; i += BATCH_SIZE) {
    const batch = keys.slice(i, i + BATCH_SIZE);

    const keysFilter = batch
      .map(k => `Ref_Key eq guid'${k}'`)
      .join(' or ');

    const url =
      `${API_BASE}Catalog_Номенклатура` +
      `?$select=Ref_Key,Description,HQC_НаименованиеEN` +
      `&$filter=${encodeURIComponent(keysFilter)}` +
      `&$format=json`;

    try {
      const response = await fetchWithRetry(url, {
        method: 'GET',
        headers: {
          Authorization: get1CAuthHeader(),
          Accept: 'application/json'
        },
        signal
      });

      if (response.ok) {
        const data = await response.json();

        (data.value || []).forEach((item: any) => {
          result[item.Ref_Key] = {
            name: item.Description || "",
            eng: item.HQC_НаименованиеEN || ""
          };
        });
      }
    } catch (e: any) {
      if (e.name === 'AbortError') throw e;
      console.warn('Nomenclature batch failed', e);
    }
  }

  return result;
};

// --- Individual document fetchers ---

const fetchMainOrders = async (orderRef: string, signal?: AbortSignal): Promise<TestOrder[]> => {
  const select = "Ref_Key,Number,Date,Posted,Комментарий,Проект_Key,Организация_Key,Контрагент_Key,Договор_Key,ЗаказПокупателя_Key,СтруктурнаяЕдиницаРезерв_Key";
  const filter = `Ref_Key eq guid'${orderRef}'`;
  const url = `${API_BASE}Document_ЗаказПоставщику?$select=${select}&$filter=${filter}&$format=json`;

  const response = await fetchWithRetry(url, {
    method: 'GET',
    headers: { 'Authorization': get1CAuthHeader(), 'Accept': 'application/json' },
    signal
  });
  if (!response.ok) throw new Error(`Main Order fetch failed: ${response.status}`);
  const data = await response.json();
  return data.value || [];
};

/** Находит Ref_Key документа по его номеру и, если задана, дате из ссылки. */
export const resolveOrderRefByNumber = async (orderNumber: string, orderDate?: string): Promise<string | null> => {
  // Старые сохранённые ссылки с GUID продолжают открываться.
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(orderNumber)) {
    return orderNumber;
  }

  const escapedNumber = orderNumber.replace(/'/g, "''");
  const filter = `Number eq '${escapedNumber}' and ВидОперации eq 'ЗаказНаПереработку' and DeletionMark eq false`;
  const url = `${API_BASE}Document_ЗаказПоставщику?$select=Ref_Key,Number,Date&$filter=${encodeURIComponent(filter)}&$format=json&$top=100`;
  const response = await fetchWithRetry(url, {
    method: 'GET',
    headers: { 'Authorization': get1CAuthHeader(), 'Accept': 'application/json' }
  });
  if (!response.ok) throw new Error(`Order lookup failed: ${response.status}`);

  const data = await response.json();
  const matches: Array<{ Ref_Key: string; Date?: string }> = data.value || [];
  const matchingOrders = orderDate
    ? matches.filter(order => (order.Date || '').slice(0, 10) === orderDate)
    : matches;

  // Номер может повторяться в разные периоды, поэтому без даты принимаем только однозначный результат.
  return matchingOrders.length === 1 ? matchingOrders[0].Ref_Key : null;
};

const fetchStockRows = async (orderRef: string, signal?: AbortSignal): Promise<any[]> => {
  const select = "LineNumber,Номенклатура_Key,Характеристика_Key,Содержание,Количество,ЕдиницаИзмерения,Цена,Сумма,СуммаНДС";
  const filter = `Ref_Key eq guid'${orderRef}'`;
  const url = `${API_BASE}Document_ЗаказПоставщику_Запасы?$select=${select}&$filter=${filter}&$format=json`;

  try {
    const response = await fetchWithRetry(url, {
      method: 'GET',
      headers: { 'Authorization': get1CAuthHeader(), 'Accept': 'application/json' },
      signal
    });
    if (response.ok) {
      const data = await response.json();
      return data.value || [];
    }
  } catch (e: any) {
    if (e.name === 'AbortError') throw e;
    console.warn(`Rows fetch failed for ${orderRef}`);
  }
  return [];
};

const fetchRelatedInvoices = async (orderRef: string, signal?: AbortSignal): Promise<PurchaseInvoice[]> => {
  const select = "Ref_Key,Number,Date,Posted,Комментарий";
  const filter = `Заказ eq cast(guid'${orderRef}', 'Document_ЗаказПоставщику') and DeletionMark eq false and ВидОперации eq 'ПоступлениеОтПоставщика'`;
  const url = `${API_BASE}Document_ПриходнаяНакладная?$select=${select}&$filter=${filter}&$orderby=Date asc&$format=json`;

  try {
    const response = await fetchWithRetry(url, {
      method: 'GET',
      headers: { 'Authorization': get1CAuthHeader(), 'Accept': 'application/json' },
      signal
    });
    if (response.ok) {
      const data = await response.json();
      return (data.value || []).map((inv: any) => ({
        Ref_Key: inv.Ref_Key,
        Number: inv.Number,
        Date: inv.Date,
        Posted: !!inv.Posted,
        Комментарий: inv.Комментарий || '',
      })) as PurchaseInvoice[];
    }
  } catch (e: any) {
    if (e.name === 'AbortError') throw e;
    console.warn(`Invoices fetch failed for ${orderRef}`, e);
  }
  return [];
};

const fetchInvoiceRows = async (invoiceRef: string, signal?: AbortSignal): Promise<InvoiceStockItem[]> => {
  const select = "Номенклатура_Key,Содержание,Количество,Сумма";
  const filter = `Ref_Key eq guid'${invoiceRef}'`;
  const url = `${API_BASE}Document_ПриходнаяНакладная_Запасы?$select=${select}&$filter=${filter}&$format=json`;

  try {
    const response = await fetchWithRetry(url, {
      method: 'GET',
      headers: { 'Authorization': get1CAuthHeader(), 'Accept': 'application/json' },
      signal
    });
    if (response.ok) {
      const data = await response.json();
      return data.value || [];
    }
  } catch (e: any) {
    if (e.name === 'AbortError') throw e;
    console.warn(`Failed to fetch items for invoice ${invoiceRef}`, e);
  }
  return [];
};

/**
 * Сумма оплат по договору/контрагенту из регистра накопления РасчетыСПоставщиками.
 * Серверный фильтр: только по Recorder_Type (АвансовыйОтчет / РасходСоСчета).
 * Клиентский фильтр: по Договор_Key и Контрагент_Key внутри RecordSet.
 * Суммируется поле СуммаРег.
 */
const fetchPaymentOrdersSum = async (
  contractKey: string,
  counterpartyKey: string,
  signal?: AbortSignal
): Promise<number> => {
  if (!contractKey || !counterpartyKey) return 0;
  const filter =
    `Recorder_Type eq 'StandardODATA.Document_АвансовыйОтчет' or Recorder_Type eq 'StandardODATA.Document_РасходСоСчета' or Recorder_Type eq 'StandardODATA.Document_РасходИзКассы'`;
  const url = `${API_BASE}AccumulationRegister_РасчетыСПоставщиками?$filter=${encodeURIComponent(filter)}&$format=json`;

  try {
    const response = await fetchWithRetry(url, {
      method: 'GET',
      headers: { 'Authorization': get1CAuthHeader(), 'Accept': 'application/json' },
      signal
    });
    if (response.ok) {
      const data = await response.json();
      let sum = 0;
      for (const item of (data.value || [])) {
        for (const rec of (item.RecordSet || [])) {
          if (rec.Договор_Key === contractKey && rec.Контрагент_Key === counterpartyKey) {
            sum += Number(rec.СуммаВал) || 0;
          }
        }
      }
      return sum;
    }
  } catch (e: any) {
    if (e.name === 'AbortError') throw e;
    console.warn("Payment sum (AccumulationRegister) fetch failed", e);
  }
  return 0;
};

// --- Contract extras: single batched request instead of 3+ separate ones ---

const LEGAL_ENTITY_PROPERTY_KEY = 'c2da0c5b-8c72-11f0-ae14-ac1f6b4129e5';
const CONTRACT_TAX_YES_NO_PROPERTY_KEY = '26cdeb77-0c74-11f1-ae33-ac1f6b4129e4';
const CONTRACT_TAX_NO_VALUE = '30e9a002-0c74-11f1-ae33-ac1f6b4129e4';
const CONTRACT_TAX_PERCENT_PROPERTY_KEY = 'e9cc7cbd-0c74-11f1-ae33-ac1f6b4129e4';
const CONTRACT_RETENTION_PCT_PROPERTY_KEY = 'b4dc0789-0c83-11f1-ae33-ac1f6b4129e4';
const CONTRACT_PREPAYMENT_PCT_PROPERTY_KEY = 'f6a70ff5-0c90-11f1-ae33-ac1f6b4129e4';
const CONTRACT_PPH_AMOUNT_PROPERTY_KEY = 'de159069-106a-11f1-ae34-ac1f6b4129e4';

interface ContractExtras {
  taxExempt: boolean;
  taxPercent: number | null;
  retentionPct: number | null;
  prepaymentPct: number | null;
  pphAmount: number | null;
  legalEntityKey: string | null;
}

const ALL_CONTRACT_PROPERTY_KEYS = [
  CONTRACT_TAX_YES_NO_PROPERTY_KEY,
  CONTRACT_TAX_PERCENT_PROPERTY_KEY,
  CONTRACT_RETENTION_PCT_PROPERTY_KEY,
  CONTRACT_PREPAYMENT_PCT_PROPERTY_KEY,
  CONTRACT_PPH_AMOUNT_PROPERTY_KEY,
  LEGAL_ENTITY_PROPERTY_KEY,
];

/**
 * Fetches all 6 additional requisites for each contract in one batched request
 * instead of 3+ separate requests per contract.
 */
const fetchAllContractExtras = async (
  contractKeys: string[],
  signal?: AbortSignal
): Promise<Record<string, ContractExtras>> => {
  const unique = [...new Set(contractKeys)].filter(Boolean);
  if (unique.length === 0) return {};

  const propertyFilter = ALL_CONTRACT_PROPERTY_KEYS.map(pk => `Свойство_Key eq guid'${pk}'`).join(' or ');

  const result: Record<string, ContractExtras> = {};
  for (const ck of unique) {
    result[ck] = { taxExempt: true, taxPercent: null, retentionPct: null, prepaymentPct: null, pphAmount: null, legalEntityKey: null };
  }

  for (let i = 0; i < unique.length; i += BATCH_SIZE) {
    const batch = unique.slice(i, i + BATCH_SIZE);
    const refFilter = batch.map(k => `Ref_Key eq guid'${k}'`).join(' or ');
    const filter = `(${refFilter}) and (${propertyFilter})`;
    const url = `${API_BASE}Catalog_ДоговорыКонтрагентов_ДополнительныеРеквизиты?$format=json&$filter=${encodeURIComponent(filter)}&$select=Ref_Key,Свойство_Key,Значение`;

    try {
      const res = await fetchWithRetry(url, {
        method: 'GET',
        headers: { 'Authorization': get1CAuthHeader(), 'Accept': 'application/json' },
        signal
      });
      if (!res.ok) continue;
      const data = await res.json();
      for (const row of (data.value || [])) {
        const ck = row.Ref_Key;
        if (!result[ck]) continue;
        const val = row.Значение;
        switch (row.Свойство_Key) {
          case CONTRACT_TAX_YES_NO_PROPERTY_KEY:
            result[ck].taxExempt = String(val || '').trim() === CONTRACT_TAX_NO_VALUE;
            break;
          case CONTRACT_TAX_PERCENT_PROPERTY_KEY: {
            const v = Number(val);
            result[ck].taxPercent = Number.isFinite(v) ? v : null;
            break;
          }
          case CONTRACT_RETENTION_PCT_PROPERTY_KEY: {
            const v = Number(val);
            result[ck].retentionPct = Number.isFinite(v) ? Math.round(v) : null;
            break;
          }
          case CONTRACT_PREPAYMENT_PCT_PROPERTY_KEY: {
            const v = Number(val);
            result[ck].prepaymentPct = Number.isFinite(v) ? Math.round(v) : null;
            break;
          }
          case CONTRACT_PPH_AMOUNT_PROPERTY_KEY: {
            const v = Number(val);
            result[ck].pphAmount = Number.isFinite(v) ? v : null;
            break;
          }
          case LEGAL_ENTITY_PROPERTY_KEY:
            result[ck].legalEntityKey = val ? String(val).trim() || null : null;
            break;
        }
      }
    } catch (e: any) {
      if (e.name === 'AbortError') throw e;
      console.warn('Contract extras batch failed', e);
    }
  }
  return result;
};

// --- Orchestrator ---

export const getTestOrder = async (orderRef: string, signal?: AbortSignal): Promise<TestOrder[]> => {
  // 1. Шапка заказа
  const orders = await fetchMainOrders(orderRef, signal);
  if (orders.length === 0) return [];

  // 2. Собираем ключи + загружаем строки и накладные
  const projectKeys: string[] = [];
  const customerOrderKeys: string[] = [];
  const counterpartyKeys: string[] = [];
  const nomenclatureKeys: string[] = [];
  const unitKeys: string[] = [];
  const contractKeys: string[] = [];
  const organizationKeys: string[] = [];
  const characteristicKeys: string[] = [];

  for (const order of orders) {
    if (order.Проект_Key) projectKeys.push(order.Проект_Key);
    if (order.ЗаказПокупателя_Key) customerOrderKeys.push(order.ЗаказПокупателя_Key);
    if (order.Контрагент_Key) counterpartyKeys.push(order.Контрагент_Key);
    if (order.Договор_Key) contractKeys.push(order.Договор_Key);
    if (order.Организация_Key) organizationKeys.push(order.Организация_Key);

    const [rawRows, invoices] = await Promise.all([
      fetchStockRows(order.Ref_Key, signal),
      fetchRelatedInvoices(order.Ref_Key, signal)
    ]);

    if (invoices.length > 0) {
      await Promise.all(invoices.map(async (inv) => {
        inv.Items = await fetchInvoiceRows(inv.Ref_Key, signal);
      }));
    }
    order.Invoices = invoices;

    order.Запасы = rawRows.map(r => {
      if (r.Номенклатура_Key) nomenclatureKeys.push(r.Номенклатура_Key);
      if (r.ЕдиницаИзмерения) unitKeys.push(r.ЕдиницаИзмерения);
      if (r.Характеристика_Key) characteristicKeys.push(r.Характеристика_Key);
      return {
        Номенклатура_Key: r.Номенклатура_Key,
        Характеристика_Key: r.Характеристика_Key,
        Содержание: r.Содержание,
        Количество: r.Количество,
        ЕдиницаИзмерения: r.ЕдиницаИзмерения,
        Цена: r.Цена,
        Сумма: r.Сумма,
        СуммаНДС: r.СуммаНДС,
        Nomenclature_Name: "Loading...",
        Unit_Name: ""
      } as TestOrderStock;
    });
  }

  // 3. Параллельная загрузка всех справочных данных + платежей + доп. реквизитов договора
  const uniqueContractKeys = [...new Set(contractKeys.filter(Boolean))];

  const [
    projects, customerOrders, counterparties, nomenclatures, units, contracts, organizations,
    characteristics, contractExtras, ...paymentSums
  ] = await Promise.all([
    fetchEntityMap("Catalog_Проекты", projectKeys, "Description", signal),
    fetchEntityMap("Document_ЗаказПокупателя", customerOrderKeys, "Number,Date", signal),
    fetchEntityMap("Catalog_Контрагенты", counterpartyKeys, "Description", signal),
    fetchNomenclatureDetails(nomenclatureKeys, signal),
    fetchEntityMap("Catalog_КлассификаторЕдиницИзмерения", unitKeys, "Description,МеждународноеСокращение", signal),
    fetchEntityMap("Catalog_ДоговорыКонтрагентов", contractKeys, "Description,НомерДоговора,Комментарий,ДатаДоговора", signal),
    fetchEntityMap("Catalog_Организации", organizationKeys, "Description", signal),
    fetchEntityMap("Catalog_ХарактеристикиНоменклатуры", characteristicKeys, "Description", signal),
    fetchAllContractExtras(uniqueContractKeys, signal),
    ...orders.map(o => fetchPaymentOrdersSum(o.Договор_Key || '', o.Контрагент_Key || '', signal))
  ]);

  // Разрешаем ключи юр. лиц → наименования (1 батч-запрос на все уникальные ключи)
  const legalEntityKeys = Object.values(contractExtras)
    .map(e => e.legalEntityKey)
    .filter((k): k is string => k != null && k !== '');
  const legalEntityNames = legalEntityKeys.length > 0
    ? await fetchEntityMap("Catalog_Контрагенты", legalEntityKeys, "Description", signal)
    : {};

  // 4. Финальный маппинг
  return orders.map((order, idx) => {
    const ck = order.Договор_Key || '';
    const extras = contractExtras[ck];
    const legalEntityName = extras?.legalEntityKey
      ? (legalEntityNames[extras.legalEntityKey]?.Description ?? null)
      : null;

    const finalOrder = {
      ...order,
      Project_Name: projects[order.Проект_Key]?.Description || order.Проект_Key,
      Counterparty_Name: counterparties[order.Контрагент_Key || '']?.Description || order.Контрагент_Key || '-',
      CustomerOrder_Number: customerOrders[order.ЗаказПокупателя_Key]
        ? `№ ${customerOrders[order.ЗаказПокупателя_Key].Number}`
        : order.ЗаказПокупателя_Key,
      Contract_Number: contracts[ck]?.НомерДоговора || contracts[ck]?.Description || "-",
      Contract_Comment: contracts[ck]?.Комментарий,
      Contract_Date: contracts[ck]?.ДатаДоговора,
      Contract_Description: contracts[ck]?.Description,
      Contract_TaxExempt: extras?.taxExempt ?? true,
      Contract_TaxPercent: extras?.taxPercent ?? null,
      Contract_PphAmount: extras?.pphAmount ?? undefined,
      Contract_RetentionPct: extras?.retentionPct ?? null,
      Contract_PrepaymentPct: extras?.prepaymentPct ?? null,
      Contract_LegalEntityName: legalEntityName,
      Organization_Name: organizations[order.Организация_Key || '']?.Description,
      PaymentOrdersSum: paymentSums[idx] as number,
    };

    if (finalOrder.Запасы) {
      finalOrder.Запасы = finalOrder.Запасы.map(stock => {
        const nom = nomenclatures[stock.Номенклатура_Key];
        const unit = units[stock.ЕдиницаИзмерения || ''];
        return {
          ...stock,
          Characteristic_Name: stock.Характеристика_Key ? (characteristics[stock.Характеристика_Key]?.Description ?? "") : "",
          Nomenclature_Name: nom?.name || stock.Номенклатура_Key,
          Nomenclature_Eng: nom?.eng || "",
          Unit_Name: unit?.МеждународноеСокращение || unit?.Description || stock.ЕдиницаИзмерения || ""
        };
      });
    }

    return finalOrder;
  });
};

// --- Invoice creation ---

const toLocalDateTime = (d: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export interface IncomingInvoiceStockRow {
  LineNumber: number;
  Номенклатура_Key: string;
  Характеристика_Key?: string;
  Количество: number;
  Сумма: number;
  Цена: number;
  ЕдиницаИзмерения: string;
  ЕдиницаИзмерения_Type: "StandardODATA.Catalog_КлассификаторЕдиницИзмерения";
  СтавкаНДС_Key: string;
  СуммаНДС?: number;
  Содержание: string;
  Всего: number;
}

export interface CreateIncomingInvoiceParams {
  orderRef: string;
  contractKey: string;
  counterpartyKey?: string;
  organizationKey?: string;
  structuralUnitKey?: string;
  projectKey?: string;
  Автор_Key?: string;
  Запасы?: IncomingInvoiceStockRow[];
  Комментарий?: string;
  Date?: string;
}

export const createIncomingInvoice = async (
  params: CreateIncomingInvoiceParams
): Promise<{ success: boolean; error?: string }> => {
  const url = `${API_BASE}Document_ПриходнаяНакладная`;

  const docDate = (params.Date && params.Date.trim()) ? new Date(params.Date.trim() + 'T12:00:00') : new Date();
  const docDateValid = !isNaN(docDate.getTime()) ? docDate : new Date();
  const body: Record<string, unknown> = {
    Date: toLocalDateTime(docDateValid),
    Posted: false,
    Договор_Key: params.contractKey,
    Заказ: params.orderRef,
    Заказ_Type: "StandardODATA.Document_ЗаказПоставщику",
    Контрагент_Key: params.counterpartyKey ?? "",
    Организация_Key: params.organizationKey ?? "",
    СтруктурнаяЕдиница_Key: params.structuralUnitKey ?? "",
    ВидОперации: "ПоступлениеОтПоставщика",
    Проект_Key: params.projectKey ?? ""
  };
  if (params.Автор_Key) {
    body.Автор_Key = params.Автор_Key;
  }
  if (params.Комментарий) {
    body.Комментарий = params.Комментарий;
  }
  if (params.Запасы && params.Запасы.length > 0) {
    body.Запасы = params.Запасы;
  }

  try {
    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Authorization': get1CAuthHeader(),
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (response.ok) {
      return { success: true };
    }
    const text = await response.text();
    return { success: false, error: `HTTP ${response.status}: ${text}` };
  } catch (e: any) {
    return { success: false, error: e.message || 'Unknown error' };
  }
};
